import React, { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { ArticleInventaire } from '@/hooks/useInventaire';
import SubCategoryIcon from './SubCategoryIcon';
import { useTranslation } from 'react-i18next';

interface RechercheArticlesProps {
  bateauId?: string;
  onArticleSelect: (article: ArticleInventaire) => void;
}

const RechercheArticles = ({ bateauId, onArticleSelect }: RechercheArticlesProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<ArticleInventaire[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { user } = useAuth();
  const { t } = useTranslation('inventory');

  useEffect(() => {
    const searchArticles = async () => {
      if (!user || !searchTerm.trim() || searchTerm.length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setIsLoading(true);
      
      try {
        let query = supabase
          .from('inventaire')
          .select(`
            *,
            espaces_stockage:espace_stockage_id (
              nom,
              type
            )
          `)
          .eq('user_id', user.id)
          .or(`nom.ilike.%${searchTerm}%,categorie.ilike.%${searchTerm}%,sous_categorie.ilike.%${searchTerm}%`)
          .order('nom')
          .limit(10);

        if (bateauId) {
          query = query.eq('bateau_id', bateauId);
        }

        const { data, error } = await query;

        if (error) {
          console.error('Erreur lors de la recherche:', error);
          setSuggestions([]);
        } else {
          setSuggestions(data as ArticleInventaire[]);
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error('Erreur lors de la recherche:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchArticles, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, user, bateauId]);

  const handleArticleClick = (article: ArticleInventaire) => {
    onArticleSelect(article);
    setSearchTerm('');
    setShowSuggestions(false);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const getStockStatus = (article: ArticleInventaire) => {
    const percentage = article.quantite_max > 0 ? (article.quantite / article.quantite_max) * 100 : 0;
    
    if (percentage === 0) return { label: t('status.empty'), color: 'bg-red-500' };
    if (percentage <= 25) return { label: t('status.critical'), color: 'bg-orange-500' };
    if (percentage < 100) return { label: t('status.partial'), color: 'bg-yellow-500' };
    return { label: t('status.full'), color: 'bg-green-500' };
  };

  return (
    <div className="relative w-full bg-gray-50 p-4 rounded-lg border border-gray-200">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder={t('rechercheArticles.placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 pr-10 bg-background"
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
        />
        {searchTerm && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearSearch}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <Card className="absolute top-full left-0 right-0 z-50 mt-1 max-h-96 overflow-y-auto shadow-lg">
          <CardContent className="p-2">
            <div className="space-y-1">
              {suggestions.map((article) => {
                const status = getStockStatus(article);
                return (
                  <div
                    key={article.id}
                    onClick={() => handleArticleClick(article)}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="flex items-center space-x-2">
                        <SubCategoryIcon 
                          sousCategorie={article.sous_categorie || article.categorie}
                          className="h-4 w-4 text-nautic"
                        />
                        <div>
                          <p className="font-medium text-sm">{article.nom}</p>
                          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                            <Badge variant="outline" className="text-xs">
                              {article.categorie}
                            </Badge>
                            {article.sous_categorie && (
                              <Badge variant="outline" className="text-xs">
                                {article.sous_categorie}
                              </Badge>
                            )}
                            {(article as any).espaces_stockage && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                                📍 {(article as any).espaces_stockage.nom}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <div className="text-right text-xs">
                        <div className="font-medium">
                          {article.quantite} / {article.quantite_max}
                        </div>
                        <div className="text-muted-foreground">
                          {Math.round((article.quantite / article.quantite_max) * 100)}%
                        </div>
                      </div>
                      <Badge 
                        variant="secondary" 
                        className={`${status.color} text-white text-xs`}
                      >
                        {status.label}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {showSuggestions && searchTerm && suggestions.length === 0 && !isLoading && (
        <Card className="absolute top-full left-0 right-0 z-50 mt-1 shadow-lg">
          <CardContent className="p-4 text-center text-muted-foreground">
            {t('rechercheArticles.noArticleFound', { term: searchTerm })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RechercheArticles;